import {
    smimeComposeFromNonPrimaryEmail,
    smimeComposeHasModernGroups,
    sharingIssueTitle,
    blockOnNavigationSendMessage,
    blockOnNavigationSendLabel,
    blockOnNavigationDontSendLabel,
    attachmentReminderText,
} from './validateSend.locstring.json';
import { smimeComposeHasCloudAttachments } from 'owa-locstrings/lib/strings/smimecomposehascloudattachments.locstring.json';
import { blockOnNavigationTitle } from 'owa-locstrings/lib/strings/blockonnavigationtitle.locstring.json';
import {
    policyTipsBlockedSendDialogTitle,
    attachmentInProgressTitle,
    missingSubjectTitle,
    missingSubjectSubTitle,
    sendAnywayButton,
    doNotSendAnywayButton,
} from '../strings.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import loc, { isStringNullOrWhiteSpace, format } from 'owa-localize';
import getCloudyAttachmentNames from './getCloudyAttachmentNames';
import { getHasSharingIssues, getSharingIssuesString } from './getHasSharingIssues';
import { getPendingAttachmentString } from './getPendingAttachmentString';
import hasModernGroupInRecipients from './hasModernGroupInRecipients';
import addInfoBarMessage from '../actions/addInfoBarMessage';
import removeInfoBarMessage from '../actions/removeInfoBarMessage';
import setIsSending from '../actions/setIsSending';
import { findComposePlugins } from '../plugins/ComposePlugins';
import { ComposeViewState } from 'owa-mail-compose-store';
import getRecipientsCount from '../utils/getRecipientsCount';
import { InfoBarMessageId } from '../utils/InfoBarMessageId';
import resolveFindControlText from '../utils/resolveFindControlText';
import { getTotalRecipientCount } from '../utils/viewStateUtils';
import { computed, IComputedValue } from 'mobx';
import { getComposeHostItemIndex, isAnyUilessAddinRunning } from 'owa-addins-core';
import { lazyAllowItemSendEvent } from 'owa-addins-core';
import { ActionDatapoint, logUsage } from 'owa-analytics';
import { confirm, ConfirmCustomizationOptions, DialogResponse } from 'owa-confirm-dialog';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isGroupTableSelected } from 'owa-group-utils';
import { IsGroupsEnabled } from 'owa-group-feature-flags';
import validateFromAddress from 'owa-mail-compose-actions/lib/utils/validateFromAddress';
import { getBlockedSendMessage } from 'owa-policy-tips/lib/utils/getBlockedSendMessage';
import { isValidToSend } from 'owa-policy-tips/lib/utils/isValidToSend';
import resolveAllUnresolvedRecipients from 'owa-readwrite-recipient-well/lib/actions/resolveAllUnresolvedRecipients';
import getNumberInvalidRecipients from 'owa-readwrite-recipient-well/lib/utils/getNumberInvalidRecipients';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { isSxSDisplayed, lazyCanCloseSxS } from 'owa-sxs-store';
import { trace } from 'owa-trace';
import {
    lazyForgottenAttachmentsToggle,
    checkForForgottenAttachments,
} from 'owa-mail-forgotten-attachments';

function markDatapointAsWaiting(datapoint: ActionDatapoint) {
    if (datapoint) {
        datapoint.addCustomData({
            waitingUserAction: true.toString(),
        });
    }
}

interface SendVerificationDialogOptions {
    text: string;
    subtext?: string;
    confirmOptions?: ConfirmCustomizationOptions;
    continueOnUserConfirmation?: boolean;
}

interface SendVerificationStep {
    getConfirmDialogOptions?: (viewState: ComposeViewState) => SendVerificationDialogOptions;
    validate: (viewState: ComposeViewState, datapoint: ActionDatapoint) => Promise<boolean>;
    messageId?: InfoBarMessageId;
    errorCode: string;
}

/** Comments in the below object reference validation steps from JsMVVM that are as-yet-implemented in owamail */
const computedValidationSteps: IComputedValue<SendVerificationStep[]> = computed(
    () =>
        [
            {
                validate: async (viewState: ComposeViewState) => {
                    return isValidToSend(viewState.policyTipsViewState);
                },
                errorCode: 'PolicyTipsViolation',
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: loc(policyTipsBlockedSendDialogTitle),
                        subtext: getBlockedSendMessage(viewState.policyTipsViewState),
                        confirmOptions: {
                            hideCancelButton: true,
                        },
                        continueOnUserConfirmation: false,
                    };
                },
            },
            /** if (this.discardManager.HasDiscarded(composeViewModel.Context.ViewModelId)) */
            /** else if (recipientComponent.IsFromRecipientInvalid) */
            {
                validate: async (viewState: ComposeViewState, datapoint: ActionDatapoint) => {
                    let hasInvalidRecipient = false;
                    for (const recipientWell of [
                        viewState.toRecipientWell,
                        viewState.ccRecipientWell,
                        viewState.bccRecipientWell,
                    ]) {
                        if (recipientWell) {
                            await resolveAllUnresolvedRecipients(
                                recipientWell,
                                recipientWell.recipients,
                                () => {
                                    markDatapointAsWaiting(datapoint);
                                }
                            );
                            if (recipientWell.inForceResolve) {
                                hasInvalidRecipient = true;
                                logUsage('sendInvalidRecipients', [
                                    getNumberInvalidRecipients(recipientWell),
                                    recipientWell.findResultSet.length,
                                ]);
                                break;
                            }
                        }
                    }

                    return !hasInvalidRecipient;
                },
                errorCode: 'InvalidRecipients',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    resolveFindControlText(viewState.toRecipientWell);
                    resolveFindControlText(viewState.ccRecipientWell);
                    resolveFindControlText(viewState.bccRecipientWell);
                    return (
                        getRecipientsCount(viewState.toRecipientWell) > 0 ||
                        getRecipientsCount(viewState.ccRecipientWell) > 0 ||
                        getRecipientsCount(viewState.bccRecipientWell) > 0
                    );
                },
                messageId: 'errorMessageNoRecipentsMailCanNotBeSend',
                errorCode: 'NoRecipients',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    return validateFromAddress(viewState);
                },
                messageId: 'errorMessageInvalidSenderMailCanNotBeSend',
                errorCode: 'InvalidSender',
            },
            {
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    const hasWaitingAttachments =
                        isFeatureEnabled('cmp-upgradeCompose') &&
                        viewState.numberOfWaitingAttachments > 0;
                    return {
                        text: loc(attachmentInProgressTitle),
                        subtext: getPendingAttachmentString(
                            viewState.attachmentWell,
                            hasWaitingAttachments
                        ),
                        confirmOptions: {
                            okText: loc(okButton),
                            hideCancelButton: true,
                        },
                        continueOnUserConfirmation: false,
                    };
                },
                validate: async (viewState: ComposeViewState) => {
                    const hasWaitingAttachments =
                        isFeatureEnabled('cmp-upgradeCompose') &&
                        viewState.numberOfWaitingAttachments > 0;
                    if (hasWaitingAttachments) {
                        return false;
                    }

                    return isStringNullOrWhiteSpace(
                        getPendingAttachmentString(viewState.attachmentWell, hasWaitingAttachments)
                    );
                },
                errorCode: 'AttachmentInProgress',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    const totalRecipients = getTotalRecipientCount(viewState);
                    return totalRecipients <= getUserConfiguration().MaxRecipientsPerMessage;
                },
                messageId: 'warningMessageMaxRecipientsExceed',
                errorCode: 'MaxRecipients',
            },
            {
                // The user might be editing an attachment in the preview pane.
                // Wait until the preview pane is either ready for send or
                // is unable to save the attachment - canClosePreviewPane
                // returns a promise to achieve this so we have to await
                // on the result of the promise
                validate: async (viewState: ComposeViewState, datapoint: ActionDatapoint) => {
                    if (!isSxSDisplayed()) {
                        return true;
                    }
                    markDatapointAsWaiting(datapoint);
                    return await (await lazyCanCloseSxS.import())();
                },
                messageId: 'errorMessageFileCanNotBeSaved',
                errorCode: 'CantClosePreviewPane',
            },
            /** else if (PolicyTipsUtilities.IsEnabled && policyTipsComponent.IsSendBlocked()) */
            {
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: loc(missingSubjectTitle),
                        subtext: loc(missingSubjectSubTitle),
                        confirmOptions: {
                            okText: loc(sendAnywayButton),
                            cancelText: loc(doNotSendAnywayButton),
                        },
                        continueOnUserConfirmation: true,
                    };
                },
                validate: async (viewState: ComposeViewState) =>
                    viewState.isInlineCompose ||
                    (viewState.subject && viewState.subject.length > 0),
                errorCode: 'NoSubject',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    removeInfoBarMessage(viewState, 'errorSmimeMessageHasIRM');
                    if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
                        const isIRMEnabled = viewState.IRMData && viewState.IRMData.RmsTemplateId;
                        return !isIRMEnabled;
                    }
                    return true;
                },
                messageId: 'errorSmimeComposeHasIRM',
                errorCode: 'HasIRMDataInSmime',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
                        return !viewState.fromViewState.isFromShown;
                    }
                    return true;
                },
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: '',
                        subtext: loc(smimeComposeFromNonPrimaryEmail),
                        confirmOptions: {
                            okText: loc(okButton),
                            hideCancelButton: true,
                        },
                        continueOnUserConfirmation: false,
                    };
                },
                errorCode: 'HasFromFieldInSmime',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
                        return !hasModernGroupInRecipients(viewState);
                    }
                    return true;
                },
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: '',
                        subtext: loc(smimeComposeHasModernGroups),
                        confirmOptions: {
                            okText: loc(okButton),
                            hideCancelButton: true,
                        },
                        continueOnUserConfirmation: false,
                    };
                },
                errorCode: 'HasModernGroupsInSmime',
            },
            {
                validate: async (viewState: ComposeViewState) => {
                    if (isSmimeEnabledInViewState(viewState.smimeViewState)) {
                        return !getCloudyAttachmentNames(viewState).length;
                    }
                    return true;
                },
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: '',
                        subtext: format(
                            loc(smimeComposeHasCloudAttachments),
                            getCloudyAttachmentNames(viewState).join(', ')
                        ),
                        confirmOptions: {
                            okText: loc(okButton),
                            hideCancelButton: true,
                        },
                        continueOnUserConfirmation: false,
                    };
                },
                errorCode: 'HasCloudyAttachmentsInSmime',
            },
            {
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: loc(sharingIssueTitle),
                        subtext: getSharingIssuesString(viewState),
                        confirmOptions: {
                            okText: loc(sendAnywayButton),
                            cancelText: loc(doNotSendAnywayButton),
                        },
                        continueOnUserConfirmation: true,
                    };
                },
                validate: async (viewState: ComposeViewState) =>
                    !(await getHasSharingIssues(viewState)),
                errorCode: 'SharingIssue',
            },
            {
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    return {
                        text: loc(blockOnNavigationTitle),
                        subtext: loc(blockOnNavigationSendMessage),
                        confirmOptions: {
                            okText: loc(blockOnNavigationSendLabel),
                            cancelText: loc(blockOnNavigationDontSendLabel),
                        },
                        continueOnUserConfirmation: true,
                    };
                },
                validate: async (viewState: ComposeViewState) => {
                    if (!isFeatureEnabled('rp-addins')) {
                        return true;
                    }
                    return !isAnyUilessAddinRunning(getComposeHostItemIndex(viewState.composeId));
                },
                errorCode: 'AddinsStillRunning',
            },
            {
                getConfirmDialogOptions: (viewState: ComposeViewState) => {
                    let forgottenAttachmentsToggle = lazyForgottenAttachmentsToggle.tryImportForRender();
                    return {
                        text: loc(attachmentReminderText),
                        confirmOptions: {
                            bodyElement: forgottenAttachmentsToggle(),
                            okText: loc(sendAnywayButton),
                            cancelText: loc(doNotSendAnywayButton),
                        },
                        continueOnUserConfirmation: true,
                    };
                },
                validate: async (viewState: ComposeViewState) => {
                    if (
                        isFeatureEnabled('doc-attachment-forgottenAttachments') &&
                        getUserConfiguration().UserOptions.CheckForForgottenAttachments
                    ) {
                        return !checkForForgottenAttachments(viewState);
                    }
                    return true;
                },
                errorCode: 'ForgottenAttachments',
            },
            {
                /** appendOnSendAsync API
                 * This appends text to the body of the e-mail after the user presses send.
                 * It must be done at this location:
                 * 1. Before ItemSend, so the appended text is also validated
                 * 2. After other validation checks, so if any of the previous validation checks
                 *  fail, the user does not see the appended text.
                 */
                validate: async (viewState: ComposeViewState) => {
                    if (
                        isFeatureEnabled('rp-addins') &&
                        isFeatureEnabled('rp-appendOnSend') &&
                        viewState.appendOnSend.length != 0
                    ) {
                        const plugins = findComposePlugins(viewState);
                        if (plugins && plugins.contentOperationPlugin) {
                            plugins.contentOperationPlugin.insertAppendOnSend(viewState.bodyType);
                            plugins.contentOperationPlugin.updateContentToViewState();
                        }
                    }
                    return true;
                },
                errorCode: 'AppendOnSend',
            },
            /** else if (attachmentComponent != null &&
                attachmentComponent.AttachmentWellViewModel != null &&
                this.ViewModel.ForgottenAttachmentDetectorAsyncFactory != null) */
            {
                /* ItemSend Event Add-ins should always be the last validation step.*/
                validate: async (viewState: ComposeViewState) => {
                    if (
                        !isFeatureEnabled('rp-addins') ||
                        (IsGroupsEnabled() && isGroupTableSelected()) ||
                        (viewState.IRMData && viewState.IRMData.RmsTemplateId)
                    ) {
                        return true;
                    }
                    const shouldAllowItemSendEvent = await lazyAllowItemSendEvent.import();
                    return await shouldAllowItemSendEvent(
                        getComposeHostItemIndex(viewState.composeId)
                    );
                },
                errorCode: 'AddinBlockingSend',
            },
        ] as SendVerificationStep[]
);

async function validateStep(
    step: SendVerificationStep,
    viewState: ComposeViewState,
    datapoint: ActionDatapoint
): Promise<boolean> {
    let validationResult = await step.validate(viewState, datapoint);

    if (!validationResult) {
        if (step.messageId) {
            addInfoBarMessage(viewState, step.messageId);
        } else if (step.getConfirmDialogOptions != undefined) {
            markDatapointAsWaiting(datapoint);
            const dialogOptions = step.getConfirmDialogOptions(viewState);
            const confirmResult: DialogResponse = await confirm(
                dialogOptions.text,
                dialogOptions.subtext,
                false /*resolveImmediately*/,
                dialogOptions.confirmOptions
            );
            validationResult =
                dialogOptions.continueOnUserConfirmation && confirmResult === DialogResponse.ok;
        }
    }

    return Promise.resolve(validationResult);
}

export default async function validateSend(
    viewState: ComposeViewState,
    datapoint: ActionDatapoint
): Promise<string> {
    const validationSteps = computedValidationSteps.get();
    for (let i = 0; i < validationSteps.length; i++) {
        try {
            if (!(await validateStep(validationSteps[i], viewState, datapoint))) {
                return Promise.resolve(validationSteps[i].errorCode);
            }
        } catch (e) {
            setIsSending(viewState, false /*isSending*/);
            trace.warn(e);
            return Promise.reject(e);
        }
    }

    return Promise.resolve(null);
}
